/**
 * SmartMessageBlockRenderer — shared Block[] renderer for block-based Smart
 * Messages (spec B3). One component drives every parent-facing surface: the
 * thread bubble, the full-page form view, and the teacher's read-only preview.
 *
 *   <SmartMessageBlockRenderer
 *     blocks={message.body}
 *     responses={recipient.responses}      // {[blockId]: {value, respondedAt}}
 *     onRespond={(blockId, value) => ...}  // async; throw to show the error
 *     onSign={(block) => ...}              // opens SignatureWidget (B5)
 *     uploadFile={(file, onProgress) => ...} // → {fileUrl, fileName}
 *     readOnly
 *   />
 *
 * Render rules (spec B3 table):
 *   text        paragraph
 *   options     radio list (choose one) / checkbox list (choose any) + Submit;
 *               two-option choose-one renders as Yes primary / No outline
 *   shortText   text field + Submit
 *   acknowledge single tick button, records true
 *   signature   "Sign" button → SignatureWidget (wired by the parent surface)
 *   attachment  file chips / image thumbs, tap to open — no response
 *   upload      "Upload" button → picker, hairline progress, retry on failure
 *   calendar    event card + "Add to calendar" (.ics download, records receipt)
 *   transport   arrangement list; the chosen row reveals the follow-up it needs
 *
 * Responses lock after the first answer — answered blocks render a quiet
 * "You answered …" confirmation instead of controls.
 *
 * Named exports: SmartMessageBlockStats (teacher per-block rollup tiles),
 * downloadEventIcs. Mobile mirror: mobile/src/components/SmartMessageBlockRenderer.jsx
 */

import { useState, useRef } from 'react';
import {
  CheckCircleIcon,
  CheckIcon,
  PaperClipIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  MapPinIcon,
  PencilIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { BLOCK_TYPES } from '@/models/smartMessageBlocks';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(iso, allDay) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dateStr = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  if (allDay) return dateStr;
  return `${dateStr}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatRespondedAt(respondedAt) {
  const d = respondedAt?.toDate?.() || (respondedAt ? new Date(respondedAt) : null);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function icsEscape(text) {
  return String(text).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

function toIcsStamp(iso, allDay) {
  const d = new Date(iso);
  if (allDay) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  }
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Build a minimal single-event .ics and trigger a browser download.
 * @param {Object} event - {title, startsAt, endsAt?, allDay, location?, notes?}
 */
export function downloadEventIcs(event) {
  const dateProp = event.allDay ? ';VALUE=DATE' : '';
  const start = toIcsStamp(event.startsAt, event.allDay);
  const end = event.endsAt
    ? toIcsStamp(event.endsAt, event.allDay)
    : event.allDay
      ? toIcsStamp(new Date(new Date(event.startsAt).getTime() + 24 * 3600 * 1000).toISOString(), true)
      : toIcsStamp(new Date(new Date(event.startsAt).getTime() + 3600 * 1000).toISOString(), false);

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Swan Schools//Smart Message//EN',
    'BEGIN:VEVENT',
    `UID:${start}-${Math.random().toString(36).slice(2, 10)}@swanschool.app`,
    `DTSTAMP:${toIcsStamp(new Date().toISOString(), false)}`,
    `DTSTART${dateProp}:${start}`,
    `DTEND${dateProp}:${end}`,
    `SUMMARY:${icsEscape(event.title || 'School event')}`,
    ...(event.location ? [`LOCATION:${icsEscape(event.location)}`] : []),
    ...(event.notes ? [`DESCRIPTION:${icsEscape(event.notes)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(event.title || 'event').replace(/[^\w -]/g, '').trim() || 'event'}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const RequiredBadge = () => (
  <span className="text-[10px] uppercase tracking-wide font-bold text-error/80 ml-2">Required</span>
);

const AnsweredLine = ({ children, respondedAt }) => {
  const when = formatRespondedAt(respondedAt);
  return (
    <p className="text-sm text-success font-medium flex items-center gap-1.5">
      <CheckCircleIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>
        {children}
        {when && <span className="text-base-content/40 font-normal"> · {when}</span>}
      </span>
    </p>
  );
};

// ─── Per-type blocks ─────────────────────────────────────────────────────────

function TextBlock({ block }) {
  if (!block.text?.trim()) return null;
  return <p className="text-sm whitespace-pre-wrap text-base-content/80">{block.text}</p>;
}

function AttachmentBlock({ block }) {
  const files = block.files || [];
  if (files.length === 0) return null;
  const images = files.filter((f) => f.mimeType?.startsWith('image/'));
  const others = files.filter((f) => !f.mimeType?.startsWith('image/'));
  return (
    <div className="space-y-2">
      {block.label && <p className="text-sm font-medium">{block.label}</p>}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((f, i) => (
            <a key={i} href={f.url} target="_blank" rel="noreferrer" className="block">
              <img
                src={f.thumbnailUrl || f.url}
                alt={f.name}
                className="h-24 w-24 object-cover rounded-xl border border-base-300"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {others.map((f, i) => (
            <a
              key={i}
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 hover:bg-base-300 text-sm max-w-full"
            >
              <PaperClipIcon className="w-4 h-4 shrink-0 opacity-60" aria-hidden="true" />
              <span className="truncate">{f.name}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function OptionsBlock({ block, response, readOnly, busy, onSubmit }) {
  const [selected, setSelected] = useState(() => (block.multi ? [] : null));

  if (response) {
    const ids = Array.isArray(response.value) ? response.value : [response.value];
    const labels = ids
      .map((id) => block.options?.find((o) => o.id === id)?.label || id)
      .join(', ');
    return (
      <div>
        <p className="text-sm font-medium mb-1">{block.label}</p>
        <AnsweredLine respondedAt={response.respondedAt}>You answered: {labels}</AnsweredLine>
      </div>
    );
  }

  // Two-option choose-one → direct Yes/No-style buttons, no submit step
  if (!block.multi && (block.options || []).length === 2) {
    return (
      <div>
        <p className="text-sm font-medium mb-2">
          {block.label}
          {block.required && <RequiredBadge />}
        </p>
        <div className="flex gap-2 flex-wrap">
          {block.options.map((opt, index) => (
            <button
              key={opt.id}
              type="button"
              className={`btn btn-sm ${index === 0 ? 'btn-primary' : 'btn-outline'}`}
              disabled={readOnly || busy}
              onClick={() => onSubmit(opt.id)}
              data-testid={`block-option-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const toggle = (optId) => {
    if (block.multi) {
      setSelected((prev) => (prev.includes(optId) ? prev.filter((x) => x !== optId) : [...prev, optId]));
    } else {
      setSelected(optId);
    }
  };
  const hasSelection = block.multi ? selected.length > 0 : !!selected;

  return (
    <div>
      <p className="text-sm font-medium mb-2">
        {block.label}
        {block.required && <RequiredBadge />}
      </p>
      <div className="space-y-1.5">
        {(block.options || []).map((opt) => {
          const isChecked = block.multi ? selected.includes(opt.id) : selected === opt.id;
          return (
            <label
              key={opt.id}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                isChecked ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-base-content/30'
              } ${readOnly ? 'cursor-default opacity-70' : ''}`}
            >
              <input
                type={block.multi ? 'checkbox' : 'radio'}
                className={block.multi ? 'checkbox checkbox-sm checkbox-primary' : 'radio radio-sm radio-primary'}
                checked={isChecked}
                disabled={readOnly || busy}
                onChange={() => toggle(opt.id)}
                data-testid={`block-option-${opt.id}`}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          );
        })}
      </div>
      {!readOnly && (
        <button
          type="button"
          className="btn btn-primary btn-sm mt-2"
          disabled={!hasSelection || busy}
          onClick={() => onSubmit(block.multi ? selected : selected)}
          data-testid={`block-submit-${block.id}`}
        >
          Submit
        </button>
      )}
    </div>
  );
}

function ShortTextBlock({ block, response, readOnly, busy, onSubmit }) {
  const [draft, setDraft] = useState('');

  if (response) {
    return (
      <div>
        <p className="text-sm font-medium mb-1">{block.label}</p>
        <AnsweredLine respondedAt={response.respondedAt}>You answered: {response.value}</AnsweredLine>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm font-medium mb-2">
        {block.label}
        {block.required && <RequiredBadge />}
      </p>
      <textarea
        className="textarea textarea-bordered w-full text-sm min-h-[4.5rem]"
        placeholder="Type your answer"
        value={draft}
        maxLength={2000}
        disabled={readOnly || busy}
        onChange={(e) => setDraft(e.target.value)}
        data-testid={`block-shorttext-${block.id}`}
      />
      {!readOnly && (
        <button
          type="button"
          className="btn btn-primary btn-sm mt-1"
          disabled={!draft.trim() || busy}
          onClick={() => onSubmit(draft.trim())}
          data-testid={`block-submit-${block.id}`}
        >
          Submit
        </button>
      )}
    </div>
  );
}

function AcknowledgeBlock({ block, response, readOnly, busy, onSubmit }) {
  if (response) {
    return <AnsweredLine respondedAt={response.respondedAt}>{block.label || 'Acknowledged'}</AnsweredLine>;
  }
  return (
    <div>
      <button
        type="button"
        className="btn btn-outline btn-sm gap-1.5"
        disabled={readOnly || busy}
        onClick={() => onSubmit(true)}
        data-testid={`block-ack-${block.id}`}
      >
        <CheckIcon className="w-4 h-4" aria-hidden="true" />
        {block.label || 'I have read this'}
        {block.required && <RequiredBadge />}
      </button>
    </div>
  );
}

function SignatureBlock({ block, response, readOnly, busy, onSign }) {
  if (response) {
    return (
      <AnsweredLine respondedAt={response.respondedAt}>
        Signed{block.label ? `: ${block.label}` : ''}
      </AnsweredLine>
    );
  }
  return (
    <div>
      <p className="text-sm font-medium mb-2">
        {block.label || 'Signature required'}
        {block.required && <RequiredBadge />}
      </p>
      <button
        type="button"
        className="btn btn-primary btn-sm gap-1.5"
        disabled={readOnly || busy || !onSign}
        onClick={() => onSign?.(block)}
        data-testid={`block-sign-${block.id}`}
      >
        <PencilIcon className="w-4 h-4" aria-hidden="true" />
        Sign
      </button>
    </div>
  );
}

function UploadBlock({ block, response, readOnly, busy, uploadFile, onSubmit, onError }) {
  const inputRef = useRef(null);
  const lastFileRef = useRef(null);
  const [progress, setProgress] = useState(null); // null | 0-100
  const [failed, setFailed] = useState(false);

  if (response) {
    return (
      <div>
        <p className="text-sm font-medium mb-1">{block.label}</p>
        <a
          href={response.value?.fileUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 hover:bg-base-300 text-sm max-w-full"
        >
          <PaperClipIcon className="w-4 h-4 shrink-0 opacity-60" aria-hidden="true" />
          <span className="truncate">{response.value?.fileName || 'Uploaded file'}</span>
        </a>
        <div className="mt-1">
          <AnsweredLine respondedAt={response.respondedAt}>File received</AnsweredLine>
        </div>
      </div>
    );
  }

  const startUpload = async (file) => {
    if (!file || !uploadFile) return;
    lastFileRef.current = file;
    setFailed(false);
    setProgress(0);
    try {
      const { fileUrl, fileName } = await uploadFile(file, setProgress);
      await onSubmit({ fileUrl, fileName });
    } catch (err) {
      setFailed(true);
      onError?.(err);
    } finally {
      setProgress(null);
    }
  };

  return (
    <div>
      <p className="text-sm font-medium mb-2">
        {block.label}
        {block.required && <RequiredBadge />}
      </p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={block.accept || undefined}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          startUpload(file);
        }}
      />
      {progress !== null ? (
        <div className="w-full max-w-xs">
          <div className="h-0.5 w-full bg-base-300 rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-base-content/50 mt-1">Uploading…</p>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline btn-sm gap-1.5"
            disabled={readOnly || busy || !uploadFile}
            onClick={() => inputRef.current?.click()}
            data-testid={`block-upload-${block.id}`}
          >
            <ArrowUpTrayIcon className="w-4 h-4" aria-hidden="true" />
            Upload
          </button>
          {failed && (
            <button
              type="button"
              className="btn btn-ghost btn-sm gap-1.5 text-error"
              onClick={() => startUpload(lastFileRef.current)}
            >
              <ArrowPathIcon className="w-4 h-4" aria-hidden="true" />
              Upload failed — retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "How will they get home?" — one arrangement, and the follow-up that
 * arrangement needs. Another adult collecting is worthless without a name, so
 * the name field appears under that row the moment it is chosen and Submit
 * stays shut until it is filled.
 *
 * Deliberately reads the block, not the activity model: the renderer stays
 * domain-neutral so anything can ask this question.
 */
function TransportBlock({ block, response, readOnly, busy, onSubmit }) {
  const [selected, setSelected] = useState(null);
  const [followUp, setFollowUp] = useState('');
  const options = block.options || [];

  if (response) {
    const value = response.value || {};
    const chosen = options.find((o) => o.id === value.option);
    const extra = value.personName || value.detail;
    return (
      <div>
        <p className="text-sm font-medium mb-1">{block.label}</p>
        <AnsweredLine respondedAt={response.respondedAt}>
          You answered: {chosen?.label || 'recorded'}
          {extra ? ` — ${extra}` : ''}
        </AnsweredLine>
      </div>
    );
  }

  const chosen = options.find((o) => o.id === selected) || null;
  const needs = chosen?.requires || null;
  const ready = !!chosen && (!needs || !!followUp.trim());

  const choose = (optId) => {
    setSelected(optId);
    setFollowUp('');
  };

  const send = () => {
    const answer = { option: chosen.id };
    if (needs === 'person') answer.personName = followUp.trim();
    if (needs === 'detail') answer.detail = followUp.trim();
    onSubmit(answer);
  };

  return (
    <div>
      <p className="text-sm font-medium mb-2">
        {block.label}
        {block.required && <RequiredBadge />}
      </p>
      <div className="space-y-1.5">
        {options.map((opt) => {
          const isChecked = selected === opt.id;
          return (
            <div key={opt.id} className="space-y-1.5">
              <label
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                  isChecked ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-base-content/30'
                } ${readOnly ? 'cursor-default opacity-70' : ''}`}
              >
                <input
                  type="radio"
                  className="radio radio-sm radio-primary"
                  checked={isChecked}
                  disabled={readOnly || busy}
                  onChange={() => choose(opt.id)}
                  data-testid={`block-transport-${opt.id}`}
                />
                <span className="min-w-0">
                  <span className="text-sm block">{opt.label}</span>
                  {opt.hint && <span className="text-xs text-base-content/60 block">{opt.hint}</span>}
                </span>
              </label>
              {isChecked && opt.requires && (
                <input
                  type="text"
                  className="input input-bordered input-sm w-full ml-6 max-w-xs text-sm"
                  maxLength={200}
                  placeholder={
                    opt.requires === 'person'
                      ? opt.personLabel || 'Who is collecting them?'
                      : opt.detailLabel || 'What is the arrangement?'
                  }
                  value={followUp}
                  disabled={readOnly || busy}
                  onChange={(e) => setFollowUp(e.target.value)}
                  data-testid={`block-transport-followup-${opt.id}`}
                  autoFocus
                />
              )}
            </div>
          );
        })}
      </div>
      {!readOnly && (
        <button
          type="button"
          className="btn btn-primary btn-sm mt-2"
          disabled={!ready || busy}
          onClick={send}
          data-testid={`block-submit-${block.id}`}
        >
          Submit
        </button>
      )}
    </div>
  );
}

function CalendarBlock({ block, response, readOnly, busy, onSubmit }) {
  const event = block.event || {};
  const dateLabel = formatDateTime(event.startsAt, event.allDay);
  const endLabel = event.endsAt ? formatDateTime(event.endsAt, event.allDay) : null;

  const handleAdd = () => {
    try {
      downloadEventIcs(event);
    } catch (err) {
      console.error('[SmartMessageBlockRenderer] ics download failed:', err);
    }
    if (!response && !readOnly) onSubmit({});
  };

  return (
    <div className="rounded-xl border border-base-300 p-3">
      {block.label && <p className="text-sm font-medium mb-1.5">{block.label}</p>}
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <CalendarDaysIcon className="w-5 h-5 text-primary" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{event.title}</p>
          {dateLabel && (
            <p className="text-xs text-base-content/60 mt-0.5">
              {dateLabel}
              {endLabel && ` – ${endLabel}`}
            </p>
          )}
          {event.location && (
            <p className="text-xs text-base-content/60 mt-0.5 flex items-center gap-1">
              <MapPinIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {event.location}
            </p>
          )}
          {event.notes && <p className="text-xs text-base-content/70 mt-1 whitespace-pre-wrap">{event.notes}</p>}
        </div>
      </div>
      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          className="btn btn-outline btn-xs gap-1"
          disabled={busy}
          onClick={handleAdd}
          data-testid={`block-calendar-${block.id}`}
        >
          <ArrowDownTrayIcon className="w-3.5 h-3.5" aria-hidden="true" />
          Add to calendar
        </button>
        {response && (
          <span className="text-xs text-success font-medium flex items-center gap-1">
            <CheckCircleIcon className="w-3.5 h-3.5" aria-hidden="true" />
            Downloaded
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main renderer ───────────────────────────────────────────────────────────

export default function SmartMessageBlockRenderer({
  blocks,
  responses = {},
  onRespond,
  onSign,
  uploadFile,
  readOnly = false,
  disabled = false,
}) {
  const [busyBlockId, setBusyBlockId] = useState(null);
  const [errors, setErrors] = useState({});

  const submit = async (blockId, value) => {
    if (readOnly || disabled || !onRespond) return;
    if (busyBlockId) return;
    setBusyBlockId(blockId);
    setErrors((prev) => ({ ...prev, [blockId]: null }));
    try {
      await onRespond(blockId, value);
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [blockId]: err?.message || 'Could not submit. Please try again.',
      }));
    } finally {
      setBusyBlockId(null);
    }
  };

  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="block-renderer">
      {blocks.map((block) => {
        const response = responses?.[block.id] || null;
        const busy = disabled || busyBlockId === block.id;
        const common = {
          block,
          response,
          readOnly,
          busy,
          onSubmit: (value) => submit(block.id, value),
        };

        let node = null;
        switch (block.type) {
          case BLOCK_TYPES.TEXT:
            node = <TextBlock block={block} />;
            break;
          case BLOCK_TYPES.ATTACHMENT:
            node = <AttachmentBlock block={block} />;
            break;
          case BLOCK_TYPES.OPTIONS:
            node = <OptionsBlock {...common} />;
            break;
          case BLOCK_TYPES.SHORT_TEXT:
            node = <ShortTextBlock {...common} />;
            break;
          case BLOCK_TYPES.ACKNOWLEDGE:
            node = <AcknowledgeBlock {...common} />;
            break;
          case BLOCK_TYPES.SIGNATURE:
            node = <SignatureBlock {...common} onSign={onSign} />;
            break;
          case BLOCK_TYPES.UPLOAD:
            node = (
              <UploadBlock
                {...common}
                uploadFile={uploadFile}
                onError={(err) =>
                  setErrors((prev) => ({ ...prev, [block.id]: err?.message || 'Upload failed.' }))
                }
              />
            );
            break;
          case BLOCK_TYPES.TRANSPORT:
            node = <TransportBlock {...common} />;
            break;
          case BLOCK_TYPES.CALENDAR:
            node = <CalendarBlock {...common} />;
            break;
          case BLOCK_TYPES.LOCATION:
            node = <LocationBlock block={block} />;
            break;
          default:
            return null;
        }
        if (node === null) return null;

        return (
          <div key={block.id} data-testid={`block-${block.id}`}>
            {node}
            {errors[block.id] && <p className="text-xs text-error mt-1">{errors[block.id]}</p>}
          </div>
        );
      })}
    </div>
  );
}

function LocationBlock({ block }) {
  const place = block.place || {};
  const mapsQuery =
    typeof place.lat === 'number' && typeof place.lng === 'number'
      ? `${place.lat},${place.lng}`
      : encodeURIComponent([place.name, place.address].filter(Boolean).join(', '));

  return (
    <div className="rounded-xl border border-base-300 p-3">
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <MapPinIcon className="w-5 h-5 text-primary" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{place.name || place.address || 'Location'}</p>
          {place.name && place.address && (
            <p className="text-xs text-base-content/60 mt-0.5">{place.address}</p>
          )}
          {mapsQuery && (
            <a
              href={`https://maps.google.com/?q=${mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary font-medium mt-1 inline-flex items-center gap-1"
              data-testid={`block-location-${block.id}`}
            >
              Open in Maps
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Teacher rollup stats ────────────────────────────────────────────────────

const STAT_TYPE_LABELS = {
  [BLOCK_TYPES.OPTIONS]: 'Question',
  [BLOCK_TYPES.SHORT_TEXT]: 'Short answer',
  [BLOCK_TYPES.ACKNOWLEDGE]: 'Acknowledgement',
  [BLOCK_TYPES.SIGNATURE]: 'Signature',
  [BLOCK_TYPES.UPLOAD]: 'File request',
  [BLOCK_TYPES.TRANSPORT]: 'Transport',
};

/**
 * Per-block response rollup tiles for the teacher (bubble expand + tracker).
 * Styled with currentColor so it reads on both bg-primary and bg-base-100.
 *
 * @param {Object} p
 * @param {Array} p.blocks - the message Block[]
 * @param {Object} p.blockResponseCounts - {[blockId]: {responded, pending, byValue?}}
 */
export function SmartMessageBlockStats({ blocks, blockResponseCounts }) {
  if (!Array.isArray(blocks)) return null;
  const statBlocks = blocks.filter((b) => STAT_TYPE_LABELS[b.type]);
  if (statBlocks.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="block-stats">
      {statBlocks.map((block) => {
        const counts = blockResponseCounts?.[block.id] || { responded: 0, pending: 0 };
        const total = (counts.responded || 0) + (counts.pending || 0);
        const pct = total > 0 ? Math.round(((counts.responded || 0) / total) * 100) : 0;
        return (
          <div key={block.id} className="rounded-xl border border-current/15 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium truncate">
                {block.label || STAT_TYPE_LABELS[block.type]}
              </p>
              <span className="text-xs opacity-70 shrink-0">
                {counts.responded || 0}/{total} answered
              </span>
            </div>
            <div className="h-1 w-full rounded-full bg-current/15 overflow-hidden mt-2">
              <div className="h-full bg-current opacity-70" style={{ width: `${pct}%` }} />
            </div>
            {/* Transport rolls up per arrangement — "14 on the bus" is the point of asking. */}
            {(block.type === BLOCK_TYPES.OPTIONS || block.type === BLOCK_TYPES.TRANSPORT) &&
              counts.byValue && (
                <div className="mt-2 space-y-0.5">
                  {(block.options || []).map((opt) => (
                    <div key={opt.id} className="flex justify-between text-xs opacity-80">
                      <span className="truncate">{opt.label}</span>
                      <span className="shrink-0 ml-2 font-medium">{counts.byValue[opt.id] || 0}</span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
}
